'use client';

import React, { useState, useMemo, useEffect } from 'react';
import { Plus, Filter, Download, RefreshCw, Calendar } from 'lucide-react';
import { toast } from 'sonner';
import LogTradeModal from './LogTradeModal';
import { loadTradesAsync, exportTradesToCSV } from '../utils/tradeStorage';

interface JournalHeaderProps {
  showFilters?: boolean;
  onFilterToggle?: () => void;
  onRefresh?: () => void;
  onTradeLogged?: () => void;
}

export default function JournalHeader({ showFilters, onFilterToggle, onRefresh, onTradeLogged }: JournalHeaderProps) {
  const [modalOpen, setModalOpen] = useState(false);
  const [dateRange, setDateRange] = useState('this-month');
  const [tradeCount, setTradeCount] = useState<number | null>(null);

  const dateLabel = useMemo(() => {
    const now = new Date();
    const monthName = now?.toLocaleString('en-US', { month: 'short' });
    const day = now?.getDate();
    const year = now?.getFullYear();
    const firstDay = 1;
    return `${monthName} ${firstDay} – ${monthName} ${day}, ${year}`;
  }, []);

  // Load trade count from storage on client only
  useEffect(() => {
    loadTradesAsync().then((trades) => setTradeCount(trades.length));
  }, []);

  const handleExport = async () => {
    const trades = await loadTradesAsync();
    if (trades.length === 0) {
      toast.error('No trades to export');
      return;
    }
    exportTradesToCSV(trades);
    toast.success(`Exported ${trades.length} trades to CSV`);
  };

  const handleRefresh = async () => {
    const trades = await loadTradesAsync();
    setTradeCount(trades.length);
    onRefresh?.();
    toast.success('Trade log refreshed');
  };

  const handleTradeLogged = async () => {
    const trades = await loadTradesAsync();
    setTradeCount(trades.length);
    onTradeLogged?.();
    setModalOpen(false);
  };

  return (
    <>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-foreground tracking-tight">Trading Journal</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {dateLabel} · <span className="text-foreground font-medium">{tradeCount !== null ? tradeCount : '—'} trades logged</span>
          </p>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {/* Date range selector */}
          <div className="flex items-center gap-1 bg-card border border-border rounded-lg px-3 py-2 text-sm text-muted-foreground">
            <Calendar size={14} className="flex-shrink-0" />
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e?.target?.value)}
              aria-label="Date range"
              className="bg-transparent text-sm text-foreground outline-none cursor-pointer"
            >
              <option value="today">Today</option>
              <option value="this-week">This Week</option>
              <option value="this-month">This Month</option>
              <option value="last-month">Last Month</option>
              <option value="ytd">Year to Date</option>
              <option value="all-time">All Time</option>
            </select>
          </div>

          <button
            onClick={onFilterToggle}
            className={`flex items-center gap-2 border rounded-lg px-3 py-2 text-sm transition-all duration-150 ${
              showFilters
                ? 'bg-primary/10 border-primary/50 text-primary' :'bg-card border-border text-muted-foreground hover:text-foreground hover:border-primary/50'
            }`}
          >
            <Filter size={14} />
            <span className="hidden sm:inline">Filter</span>
          </button>

          <button
            onClick={handleExport}
            className="flex items-center gap-2 bg-card border border-border rounded-lg px-3 py-2 text-sm text-muted-foreground hover:text-foreground hover:border-primary/50 transition-all duration-150"
          >
            <Download size={14} />
            <span className="hidden sm:inline">Export</span>
          </button>

          <button
            aria-label="Refresh"
            onClick={handleRefresh}
            className="flex items-center gap-2 bg-card border border-border rounded-lg px-3 py-2 text-sm text-muted-foreground hover:text-foreground hover:border-primary/50 transition-all duration-150"
          >
            <RefreshCw size={14} />
          </button>

          <button
            onClick={() => setModalOpen(true)}
            className="flex items-center gap-2 bg-primary text-primary-foreground rounded-lg px-4 py-2 text-sm font-600 hover:bg-primary/90 active:scale-95 transition-all duration-150 shadow-sm"
          >
            <Plus size={16} />
            <span>Log Trade</span>
          </button>
        </div>
      </div>
      {/* Strategy filter chips */}
      <div className="flex items-center gap-2 flex-wrap mt-4">
        {['All Strategies', 'Breakout', 'Mean Reversion', 'Momentum', 'Gap Fill', 'VWAP Reclaim']?.map((tag, i) => (
          <button
            key={`strategy-chip-${tag?.replace(/\s+/g, '-')?.toLowerCase()}`}
            className={`px-3 py-1.5 rounded-full text-xs font-500 transition-all duration-150 border ${
              i === 0
                ? 'bg-primary/10 text-primary border-primary/30' :'bg-card text-muted-foreground border-border hover:border-primary/30 hover:text-foreground'
            }`}
          >
            {tag}
          </button>
        ))}
      </div>
      <LogTradeModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSaved={handleTradeLogged}
      />
    </>
  );
}